import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { Shield, Activity, FileText, RotateCcw, Lock, TrendingUp, Key, GitBranch, Cpu, Settings, ChevronRight, CheckCircle, XCircle, AlertTriangle, Download, RefreshCw, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sub-components
const ScoreGauge = ({ score, grade, label, size = 'lg' }) => {
  const color = score >= 80 ? 'hsl(var(--success))' : score >= 60 ? 'hsl(var(--warning))' : 'hsl(var(--destructive))';
  const radius = size === 'lg' ? 54 : 38;
  const stroke = size === 'lg' ? 8 : 6;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const dim = (radius + stroke) * 2;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
      <svg width={dim} height={dim} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke="hsl(var(--border))" strokeWidth={stroke} />
        <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke={color}
          strokeWidth={stroke} strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div style={{ position: 'relative', marginTop: size === 'lg' ? '-80px' : '-56px', textAlign: 'center', marginBottom: size === 'lg' ? '16px' : '8px' }}>
        <div style={{ fontSize: size === 'lg' ? '1.75rem' : '1.25rem', fontWeight: 700, color: 'hsl(var(--foreground))', letterSpacing: '-0.02em' }}>{score}%</div>
        {grade && <div style={{ fontSize: '0.75rem', fontWeight: 600, color, letterSpacing: '0.05em' }}>{grade}</div>}
      </div>
      {label && <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', fontWeight: 500 }}>{label}</div>}
    </div>
  );
};

const GateStatus = ({ gate }) => {
  const icon = gate.status === 'pass' ? <CheckCircle size={16} style={{ color: 'hsl(var(--success))' }} /> :
    gate.status === 'block' ? <XCircle size={16} style={{ color: 'hsl(var(--destructive))' }} /> :
      <AlertTriangle size={16} style={{ color: 'hsl(var(--warning))' }} />;

  return (
    <div data-testid={`gate-${gate.name.toLowerCase().replace(/\s/g, '-')}`}
      style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: gate.status === 'block' ? 'hsl(0 72% 54% / 0.06)' : 'hsl(var(--secondary))', borderRadius: '0.5rem', border: '1px solid hsl(var(--border))' }}>
      {icon}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'hsl(var(--foreground))' }}>{gate.name}</div>
        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{gate.detail}</div>
      </div>
      <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: gate.status === 'pass' ? 'hsl(var(--success))' : gate.status === 'block' ? 'hsl(var(--destructive))' : 'hsl(var(--warning))' }}>
        {gate.status}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub, accent }) => (
  <div style={{ padding: '1.25rem', background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))' }}>
    <div style={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>{label}</div>
    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: accent || 'hsl(var(--foreground))', letterSpacing: '-0.02em' }}>{value}</div>
    {sub && <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>{sub}</div>}
  </div>
);

export default function GovernancePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [auditLogs, setAuditLogs] = useState({ logs: [], total: 0 });
  const [trends, setTrends] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [gateResult, setGateResult] = useState(null);
  const [runtimeReport, setRuntimeReport] = useState(null);
  const [buildConfig, setBuildConfig] = useState(null);
  const [modelRouting, setModelRouting] = useState(null);
  const [cicdResult, setCicdResult] = useState(null);
  const [secretReport, setSecretReport] = useState(null);
  const [loading, setLoading] = useState({});

  const setLoadingKey = (key, val) => setLoading(prev => ({ ...prev, [key]: val }));

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/governance/dashboard`, { credentials: 'include' });
      if (res.ok) setDashboard(await res.json());
    } catch {}
  }, []);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/audit-logs?limit=100`, { credentials: 'include' });
      if (res.ok) setAuditLogs(await res.json());
    } catch {}
  }, []);

  const fetchTrends = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/intelligence/trends?days=90`, { credentials: 'include' });
      if (res.ok) setTrends(await res.json());
    } catch {}
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Sort: completed first, then by name
        const sorted = data.sort((a, b) => {
          if (a.status === 'completed' && b.status !== 'completed') return -1;
          if (a.status !== 'completed' && b.status === 'completed') return 1;
          return (a.name || '').localeCompare(b.name || '');
        });
        setProjects(sorted);
        if (sorted.length > 0 && !selectedProject) setSelectedProject(sorted[0].id);
      }
    } catch {}
  }, [selectedProject]);

  useEffect(() => {
    fetchDashboard();
    fetchAuditLogs();
    fetchTrends();
    fetchProjects();
    // Seed audit logs on first load
    fetch(`${API_URL}/api/governance/seed-audit-logs`, { method: 'POST', credentials: 'include' })
      .then(() => fetchAuditLogs())
      .catch(() => {});
  }, [fetchDashboard, fetchAuditLogs, fetchTrends, fetchProjects]);

  // Auto-load project-specific data when tab changes
  useEffect(() => {
    if (!selectedProject) return;
    if (activeTab === 'gates' && !gateResult) checkDeploymentGate();
    if (activeTab === 'runtime' && !runtimeReport) runRuntimeValidation();
    if (activeTab === 'replay' && !buildConfig) getBuildConfig();
    if (activeTab === 'secrets' && !secretReport) scanSecrets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedProject]);

  // Project-specific actions
  const runRuntimeValidation = async () => {
    if (!selectedProject) return;
    setLoadingKey('runtime', true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${selectedProject}/runtime-validate`, { method: 'POST', credentials: 'include' });
      if (res.ok) setRuntimeReport(await res.json());
    } catch {} finally { setLoadingKey('runtime', false); }
  };

  const checkDeploymentGate = async () => {
    if (!selectedProject) return;
    setLoadingKey('gate', true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${selectedProject}/deployment-gate`, { credentials: 'include' });
      if (res.ok) setGateResult(await res.json());
    } catch {} finally { setLoadingKey('gate', false); }
  };

  const getBuildConfig = async () => {
    if (!selectedProject) return;
    setLoadingKey('config', true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${selectedProject}/build-config`, { credentials: 'include' });
      if (res.ok) setBuildConfig(await res.json());
    } catch {} finally { setLoadingKey('config', false); }
  };

  const scanSecrets = async () => {
    if (!selectedProject) return;
    setLoadingKey('secrets', true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${selectedProject}/secrets/scan`, { method: 'POST', credentials: 'include' });
      if (res.ok) setSecretReport(await res.json());
    } catch {} finally { setLoadingKey('secrets', false); }
  };

  const exportCICD = async (provider) => {
    if (!selectedProject) return;
    setLoadingKey('cicd', true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${selectedProject}/export-cicd`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider }),
      });
      if (res.ok) setCicdResult(await res.json());
    } catch {} finally { setLoadingKey('cicd', false); }
  };

  // Auto-export GitHub Actions when CI/CD tab opens
  useEffect(() => {
    if (activeTab === 'cicd' && selectedProject && !cicdResult) {
      exportCICD('github_actions');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedProject]);

  const exportAuditLogs = async () => {
    window.open(`${API_URL}/api/audit-logs/export`, '_blank');
  };

  const fetchModelRouting = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/model-routing`, { credentials: 'include' });
      if (res.ok) setModelRouting(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    if (activeTab === 'model-routing') fetchModelRouting();
  }, [activeTab, fetchModelRouting]);

  const tabs = [
    { id: 'dashboard', label: 'Health Index', icon: Activity },
    { id: 'gates', label: 'Deploy Gates', icon: Shield },
    { id: 'audit', label: 'Audit Trail', icon: FileText },
    { id: 'runtime', label: 'Runtime Validation', icon: Cpu },
    { id: 'replay', label: 'Build Replay', icon: RotateCcw },
    { id: 'intelligence', label: 'Intelligence', icon: TrendingUp },
    { id: 'secrets', label: 'Secrets', icon: Key },
    { id: 'cicd', label: 'CI/CD Sync', icon: GitBranch },
    { id: 'model-routing', label: 'Model Routing', icon: Settings },
  ];

  const projectSelector = (
    <div style={{ marginBottom: '1.5rem' }}>
      <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '0.375rem' }}>Project</label>
      <select data-testid="governance-project-selector" value={selectedProject} onChange={e => { setSelectedProject(e.target.value); setGateResult(null); setRuntimeReport(null); setBuildConfig(null); setSecretReport(null); setCicdResult(null); }}
        style={{ width: '100%', maxWidth: 420, padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--foreground))', fontSize: '0.875rem' }}>
        {projects.map(p => <option key={p.id} value={p.id}>{p.name} ({p.status})</option>)}
      </select>
    </div>
  );

  return (
    <div data-testid="governance-page" style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'hsl(var(--foreground))', letterSpacing: '-0.02em', marginBottom: '0.25rem' }}>
          <Shield size={20} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'text-bottom', color: 'hsl(var(--accent))' }} />
          Governance & Trust
        </h1>
        <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Runtime trust, org-level control, auditability, and deployment intelligence</p>
      </div>

      {/* Tab Navigation */}
      <div data-testid="governance-tabs" style={{ display: 'flex', gap: '0.25rem', overflowX: 'auto', borderBottom: '1px solid hsl(var(--border))', marginBottom: '1.5rem', paddingBottom: '0' }}>
        {tabs.map(tab => (
          <button key={tab.id} data-testid={`governance-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.625rem 0.875rem', fontSize: '0.8125rem', fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? 'hsl(var(--accent))' : 'hsl(var(--muted-foreground))',
              background: 'none', border: 'none', borderBottom: activeTab === tab.id ? '2px solid hsl(var(--accent))' : '2px solid transparent',
              cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s ease',
            }}>
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div data-testid="governance-dashboard-tab">
          {dashboard ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', marginBottom: '2rem', padding: '1.5rem', background: 'hsl(var(--card))', borderRadius: '0.75rem', border: '1px solid hsl(var(--border))' }}>
                <ScoreGauge score={dashboard.engineering_health_index} grade={dashboard.health_grade} label="Engineering Health Index" />
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.75rem' }}>Org Engineering Health Index</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                    <div><div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase' }}>Build Confidence</div><div style={{ fontSize: '1.125rem', fontWeight: 700 }}>{dashboard.health_components.build_confidence}%</div></div>
                    <div><div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase' }}>Runtime</div><div style={{ fontSize: '1.125rem', fontWeight: 700 }}>{dashboard.health_components.runtime_confidence}%</div></div>
                    <div><div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase' }}>Success Rate</div><div style={{ fontSize: '1.125rem', fontWeight: 700 }}>{dashboard.health_components.success_rate}%</div></div>
                    <div><div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase' }}>Security</div><div style={{ fontSize: '1.125rem', fontWeight: 700 }}>{dashboard.health_components.security_posture}%</div></div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <StatCard label="Total Projects" value={dashboard.summary.total_projects} />
                <StatCard label="Completed Builds" value={dashboard.summary.completed_builds} accent="hsl(var(--success))" />
                <StatCard label="Failed Builds" value={dashboard.summary.failed_builds} accent="hsl(var(--destructive))" />
                <StatCard label="Retry Rate" value={`${dashboard.summary.retry_rate}%`} />
                <StatCard label="Policy Violations" value={dashboard.policy_violations} accent={dashboard.policy_violations > 0 ? 'hsl(var(--destructive))' : 'hsl(var(--success))'} />
                <StatCard label="Monthly Cost" value={`$${dashboard.cost.last_30_days}`} sub={`${dashboard.cost.builds_last_30_days} builds`} />
              </div>

              <div style={{ padding: '1.25rem', background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))' }}>
                <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Security Findings (All Projects)</h3>
                <div style={{ display: 'flex', gap: '1.5rem' }}>
                  {Object.entries(dashboard.security).map(([sev, count]) => (
                    <div key={sev} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: sev === 'critical' ? 'hsl(var(--destructive))' : sev === 'high' ? 'hsl(var(--warning))' : 'hsl(var(--muted-foreground))' }}>{count}</div>
                      <div style={{ fontSize: '0.6875rem', textTransform: 'capitalize', color: 'hsl(var(--muted-foreground))' }}>{sev}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'hsl(var(--muted-foreground))' }}>Loading governance data...</div>
          )}
        </div>
      )}

      {/* Deployment Gates Tab */}
      {activeTab === 'gates' && (
        <div data-testid="governance-gates-tab">
          {projectSelector}
          <button data-testid="check-deployment-gate-btn" onClick={checkDeploymentGate} disabled={loading.gate || !selectedProject}
            style={{ padding: '0.5rem 1.25rem', background: 'hsl(var(--accent))', color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', marginBottom: '1.5rem', opacity: loading.gate ? 0.6 : 1 }}>
            {loading.gate ? 'Checking...' : 'Check Deployment Gates'}
          </button>

          {gateResult ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', padding: '1rem', borderRadius: '0.5rem', background: gateResult.can_deploy ? 'hsl(160 65% 34% / 0.08)' : 'hsl(0 72% 54% / 0.06)', border: `1px solid ${gateResult.can_deploy ? 'hsl(var(--success))' : 'hsl(var(--destructive))'}` }}>
                {gateResult.can_deploy ? <CheckCircle size={20} style={{ color: 'hsl(var(--success))' }} /> : <XCircle size={20} style={{ color: 'hsl(var(--destructive))' }} />}
                <div>
                  <div style={{ fontSize: '0.9375rem', fontWeight: 700 }} data-testid="gate-result">{gateResult.can_deploy ? 'Deployment Approved' : 'Deployment Blocked'}</div>
                  <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{gateResult.pass_count} passed, {gateResult.block_count} blocked, {gateResult.warn_count} warnings</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {gateResult.gates.map((g, i) => <GateStatus key={i} gate={g} />)}
              </div>
            </div>
          ) : loading.gate ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'hsl(var(--muted-foreground))' }}>Evaluating deployment gates...</div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))' }}>
              <Shield size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.3 }} />
              <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>Select a project to check deployment gates</div>
              <div style={{ fontSize: '0.75rem' }}>Gates evaluate build confidence, tests, security, and runtime readiness before allowing deployment.</div>
            </div>
          )}
        </div>
      )}

      {/* Audit Trail Tab */}
      {activeTab === 'audit' && (
        <div data-testid="governance-audit-tab">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600 }}>Audit Log ({auditLogs.total} entries)</h2>
            <button data-testid="export-audit-logs-btn" onClick={exportAuditLogs}
              style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.75rem', background: 'hsl(var(--secondary))', border: '1px solid hsl(var(--border))', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer', color: 'hsl(var(--foreground))' }}>
              <Download size={12} /> Export JSON
            </button>
          </div>
          <div style={{ background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--secondary))' }}>
                  <th style={{ textAlign: 'left', padding: '0.625rem 1rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', fontSize: '0.6875rem', textTransform: 'uppercase' }}>Timestamp</th>
                  <th style={{ textAlign: 'left', padding: '0.625rem 1rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', fontSize: '0.6875rem', textTransform: 'uppercase' }}>Action</th>
                  <th style={{ textAlign: 'left', padding: '0.625rem 1rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', fontSize: '0.6875rem', textTransform: 'uppercase' }}>Project</th>
                  <th style={{ textAlign: 'left', padding: '0.625rem 1rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', fontSize: '0.6875rem', textTransform: 'uppercase' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.logs.length === 0 ? (
                  <tr><td colSpan={4} style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>No audit logs yet. Actions will be recorded as you use the platform.</td></tr>
                ) : auditLogs.logs.map((log, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                    <td style={{ padding: '0.5rem 1rem', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{new Date(log.timestamp).toLocaleString()}</td>
                    <td style={{ padding: '0.5rem 1rem' }}>
                      <span style={{ padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.6875rem', fontWeight: 600, background: log.action.includes('block') ? 'hsl(0 72% 54% / 0.1)' : log.action.includes('complete') ? 'hsl(160 65% 34% / 0.1)' : 'hsl(var(--secondary))', color: log.action.includes('block') ? 'hsl(var(--destructive))' : log.action.includes('complete') ? 'hsl(var(--success))' : 'hsl(var(--foreground))' }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{log.project_id ? log.project_id.substring(0, 8) + '...' : '-'}</td>
                    <td style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{JSON.stringify(log.details).substring(0, 80)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Runtime Validation Tab */}
      {activeTab === 'runtime' && (
        <div data-testid="governance-runtime-tab">
          {projectSelector}
          <button data-testid="run-runtime-validation-btn" onClick={runRuntimeValidation} disabled={loading.runtime || !selectedProject}
            style={{ padding: '0.5rem 1.25rem', background: 'hsl(var(--accent))', color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', marginBottom: '1.5rem', opacity: loading.runtime ? 0.6 : 1 }}>
            {loading.runtime ? 'Validating...' : 'Run Runtime Validation'}
          </button>

          {runtimeReport ? (
            <div>
              <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <ScoreGauge score={runtimeReport.runtime_confidence_score} grade={runtimeReport.grade} label="Runtime Confidence" size="sm" />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                    <StatCard label="Passed" value={runtimeReport.pass_count} accent="hsl(var(--success))" />
                    <StatCard label="Warnings" value={runtimeReport.warn_count} accent="hsl(var(--warning))" />
                    <StatCard label="Failed" value={runtimeReport.fail_count} accent="hsl(var(--destructive))" />
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {runtimeReport.checks.map((check, i) => (
                  <div key={i} data-testid={`runtime-check-${check.name.toLowerCase().replace(/\s/g, '-')}`}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: 'hsl(var(--card))', borderRadius: '0.5rem', border: '1px solid hsl(var(--border))' }}>
                    {check.status === 'pass' ? <CheckCircle size={16} style={{ color: 'hsl(var(--success))' }} /> : check.status === 'warn' ? <AlertTriangle size={16} style={{ color: 'hsl(var(--warning))' }} /> : <XCircle size={16} style={{ color: 'hsl(var(--destructive))' }} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{check.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{check.detail}</div>
                    </div>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 700, fontFamily: 'var(--font-mono, monospace)' }}>{check.score}/{check.status === 'pass' ? check.score : '?'}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : loading.runtime ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'hsl(var(--muted-foreground))' }}>Running runtime validation checks...</div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))' }}>
              <Cpu size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.3 }} />
              <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>Runtime validation will run automatically</div>
              <div style={{ fontSize: '0.75rem' }}>Checks package health, environment consistency, port bindings, and dependency freshness.</div>
            </div>
          )}
        </div>
      )}

      {/* Build Replay Tab */}
      {activeTab === 'replay' && (
        <div data-testid="governance-replay-tab">
          {projectSelector}
          <button data-testid="get-build-config-btn" onClick={getBuildConfig} disabled={loading.config || !selectedProject}
            style={{ padding: '0.5rem 1.25rem', background: 'hsl(var(--secondary))', color: 'hsl(var(--foreground))', border: '1px solid hsl(var(--border))', borderRadius: '0.375rem', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', marginBottom: '1.5rem' }}>
            {loading.config ? 'Loading...' : 'Load Build Configuration'}
          </button>

          {buildConfig ? (
            <div style={{ background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))', padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '0.9375rem', fontWeight: 600 }}>Frozen Build Configuration</h3>
                <span style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem', background: 'hsl(var(--accent) / 0.1)', color: 'hsl(var(--accent))', borderRadius: '9999px', fontWeight: 600 }}>Deterministic</span>
              </div>
              <pre data-testid="build-config-json" style={{ background: 'hsl(222 35% 16%)', color: 'hsl(160 65% 70%)', padding: '1rem', borderRadius: '0.5rem', fontSize: '0.75rem', overflow: 'auto', maxHeight: 400, lineHeight: 1.6 }}>
                {JSON.stringify(buildConfig.build_config, null, 2)}
              </pre>
              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
                <button data-testid="replay-build-btn"
                  style={{ padding: '0.5rem 1rem', background: 'hsl(var(--accent))', color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <RotateCcw size={14} /> Replay Build
                </button>
              </div>
            </div>
          ) : loading.config ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'hsl(var(--muted-foreground))' }}>Loading build configuration...</div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))' }}>
              <RotateCcw size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.3 }} />
              <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>Build configuration loading</div>
              <div style={{ fontSize: '0.75rem' }}>Frozen build configs enable deterministic replays of any previous build.</div>
            </div>
          )}
        </div>
      )}

      {/* Intelligence Trends Tab */}
      {activeTab === 'intelligence' && (
        <div data-testid="governance-intelligence-tab">
          {trends ? (
            <>
              {/* Regression Risk */}
              <div style={{ padding: '1.25rem', background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))', marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.75rem' }}>Regression Risk Assessment</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: trends.regression_risk.level === 'High' ? 'hsl(var(--destructive))' : trends.regression_risk.level === 'Medium' ? 'hsl(var(--warning))' : 'hsl(var(--success))' }}>
                    {trends.regression_risk.percentage}%
                  </div>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{trends.regression_risk.level} Risk</div>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{trends.regression_risk.recent_failures} failures in {trends.regression_risk.recent_builds} recent builds</div>
                  </div>
                </div>
              </div>

              {/* Confidence Trend */}
              <div style={{ padding: '1.25rem', background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))', marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.75rem' }}>Confidence Trend (Weekly)</h3>
                {trends.confidence_trend.length > 0 ? (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', height: 120, paddingTop: '0.5rem' }}>
                    {trends.confidence_trend.map((d, i) => (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                        <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>{d.avg_confidence}%</div>
                        <div style={{ width: '100%', maxWidth: 40, height: `${Math.max(d.avg_confidence, 5)}%`, background: d.avg_confidence >= 70 ? 'hsl(var(--accent))' : d.avg_confidence >= 50 ? 'hsl(var(--warning))' : 'hsl(var(--destructive))', borderRadius: '0.25rem 0.25rem 0 0', transition: 'height 0.3s ease' }} />
                        <div style={{ fontSize: '0.5625rem', color: 'hsl(var(--muted-foreground))', writingMode: 'vertical-rl', textOrientation: 'mixed', height: 40 }}>{d.week}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: '1rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', fontSize: '0.8125rem' }}>No trend data yet. Build more projects to see confidence trends.</div>
                )}
              </div>

              {/* Repair Trend */}
              <div style={{ padding: '1.25rem', background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))' }}>
                <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.75rem' }}>Repair Rate Trend</h3>
                {trends.repair_trend.length > 0 ? (
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {trends.repair_trend.map((d, i) => (
                      <div key={i} style={{ padding: '0.5rem 0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem', fontSize: '0.75rem' }}>
                        <div style={{ fontWeight: 600 }}>{d.week}</div>
                        <div style={{ color: 'hsl(var(--muted-foreground))' }}>{d.repairs} repairs / {d.builds} builds</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: '1rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', fontSize: '0.8125rem' }}>No repair data yet.</div>
                )}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'hsl(var(--muted-foreground))' }}>Loading intelligence data...</div>
          )}
        </div>
      )}

      {/* Secrets Tab */}
      {activeTab === 'secrets' && (
        <div data-testid="governance-secrets-tab">
          {projectSelector}
          <button data-testid="scan-secrets-btn" onClick={scanSecrets} disabled={loading.secrets || !selectedProject}
            style={{ padding: '0.5rem 1.25rem', background: 'hsl(var(--accent))', color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', marginBottom: '1.5rem', opacity: loading.secrets ? 0.6 : 1 }}>
            {loading.secrets ? 'Scanning...' : 'Scan for Secrets'}
          </button>

          {secretReport ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                <StatCard label="Total Findings" value={secretReport.total_findings} />
                <StatCard label="Critical" value={secretReport.critical} accent="hsl(var(--destructive))" />
                <StatCard label="High" value={secretReport.high} accent="hsl(var(--warning))" />
              </div>

              {secretReport.findings.length > 0 ? (
                <div style={{ background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))', padding: '1.25rem', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Findings</h3>
                  {secretReport.findings.map((f, i) => (
                    <div key={i} style={{ padding: '0.625rem 0', borderBottom: i < secretReport.findings.length - 1 ? '1px solid hsl(var(--border))' : 'none', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                      <Lock size={14} style={{ color: f.severity === 'critical' ? 'hsl(var(--destructive))' : 'hsl(var(--warning))', marginTop: 2 }} />
                      <div>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{f.type} in <code style={{ fontSize: '0.75rem', background: 'hsl(var(--secondary))', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>{f.file}</code></div>
                        <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>Line {f.line} | Replace with: <code>{f.suggested_env_var}</code></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '1.5rem', textAlign: 'center', background: 'hsl(160 65% 34% / 0.06)', borderRadius: '0.625rem', border: '1px solid hsl(var(--success))', marginBottom: '1.5rem' }}>
                  <CheckCircle size={24} style={{ margin: '0 auto 0.5rem', color: 'hsl(var(--success))' }} />
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'hsl(var(--success))' }}>No secrets found in codebase</div>
                  <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>All sensitive values are properly externalized to environment variables.</div>
                </div>
              )}

              {secretReport.env_template && (
                <div style={{ background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))', padding: '1.25rem' }}>
                  <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Generated .env Template</h3>
                  <pre style={{ background: 'hsl(222 35% 16%)', color: 'hsl(160 65% 70%)', padding: '1rem', borderRadius: '0.5rem', fontSize: '0.75rem', overflow: 'auto', maxHeight: 200 }}>
                    {secretReport.env_template}
                  </pre>
                </div>
              )}
            </div>
          ) : loading.secrets ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'hsl(var(--muted-foreground))' }}>Scanning for exposed secrets...</div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))' }}>
              <Key size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.3 }} />
              <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>Secrets scan loading</div>
              <div style={{ fontSize: '0.75rem' }}>Scans generated code for hardcoded API keys, tokens, passwords, and connection strings.</div>
            </div>
          )}
        </div>
      )}

      {/* CI/CD Sync Tab */}
      {activeTab === 'cicd' && (
        <div data-testid="governance-cicd-tab">
          {projectSelector}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {[{ id: 'github_actions', label: 'GitHub Actions' }, { id: 'gitlab_ci', label: 'GitLab CI' }, { id: 'bitbucket_pipelines', label: 'Bitbucket' }].map(p => (
              <button key={p.id} data-testid={`export-cicd-${p.id}-btn`} onClick={() => exportCICD(p.id)} disabled={loading.cicd || !selectedProject}
                style={{ padding: '0.5rem 1rem', background: 'hsl(var(--secondary))', color: 'hsl(var(--foreground))', border: '1px solid hsl(var(--border))', borderRadius: '0.375rem', fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <GitBranch size={14} /> {p.label}
              </button>
            ))}
          </div>

          {cicdResult && (
            <div style={{ background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))', padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '0.9375rem', fontWeight: 600 }}>{cicdResult.filename}</h3>
                <span style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem', background: 'hsl(var(--accent) / 0.1)', color: 'hsl(var(--accent))', borderRadius: '9999px', fontWeight: 600 }}>{cicdResult.provider}</span>
              </div>
              <pre data-testid="cicd-config-content" style={{ background: 'hsl(222 35% 16%)', color: 'hsl(160 65% 70%)', padding: '1rem', borderRadius: '0.5rem', fontSize: '0.75rem', overflow: 'auto', maxHeight: 400, lineHeight: 1.5 }}>
                {cicdResult.content}
              </pre>
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                {cicdResult.instructions}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Model Routing Tab */}
      {activeTab === 'model-routing' && (
        <div data-testid="governance-model-routing-tab">
          <div style={{ padding: '1.25rem', background: 'hsl(var(--card))', borderRadius: '0.625rem', border: '1px solid hsl(var(--border))', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.5rem' }}>Private Model Routing</h3>
            <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
              Route AI agent calls through your own LLM endpoints. Available on Team and Enterprise plans.
            </p>

            {modelRouting ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: modelRouting.enabled ? 'hsl(var(--success))' : 'hsl(var(--muted-foreground))' }} />
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{modelRouting.enabled ? 'Enabled' : 'Disabled'}</span>
                </div>

                {modelRouting.routes && Object.keys(modelRouting.routes).length > 0 ? (
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {Object.entries(modelRouting.routes).map(([agent, config]) => (
                      <div key={agent} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem' }}>
                        <div>
                          <div style={{ fontSize: '0.8125rem', fontWeight: 600, textTransform: 'capitalize' }}>{agent} Agent</div>
                          <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>{config.provider} / {config.model}</div>
                        </div>
                        <ChevronRight size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: '1rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', fontSize: '0.8125rem' }}>
                    {modelRouting.message || 'No custom routes configured. Using default Anthropic Claude.'}
                  </div>
                )}

                <div style={{ marginTop: '1.25rem', padding: '0.75rem', background: 'hsl(34 92% 52% / 0.08)', borderRadius: '0.375rem', fontSize: '0.75rem', color: 'hsl(34 60% 35%)' }}>
                  <strong>Supported providers:</strong> Anthropic (Claude), OpenAI (GPT), Azure OpenAI, Google (Gemini), Custom (bring-your-own endpoint)
                </div>
              </div>
            ) : (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>Loading...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
